import i18n from '@/utils/i18n';
import { InboundAssigneeOption } from '@/screens/order-inbounds/order-inbounds-order-list/types';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useInboundAssigneeOptionsQuery } from '@/screens/order-inbounds/queries';
import { useMemo } from 'react';

/**
 * Assignee checkbox options for the inbound list's filter panel, ordered the
 * way the panel renders them: the current user first with a "(Me)" suffix, the
 * rest alphabetical.
 *
 * The backend already resolves each display name (supplier employee override
 * when the org has one, else the employee's own name), so this only sorts and
 * decorates.
 */
export const useInboundAssigneeOptions = () => {
  const { user } = useAuthenticatedUserContext();
  const { data, error, loading } = useInboundAssigneeOptionsQuery();

  const options = useMemo<InboundAssigneeOption[]>(() => {
    const assignees = data?.getSmartOrderInboundAssignees?.assignees ?? [];

    // Copied before sorting: the query result is frozen by the Apollo cache.
    return [...assignees]
      .sort((a, b) => {
        const aIsMe = a.employee_id === user.uuid;
        const bIsMe = b.employee_id === user.uuid;

        if (aIsMe !== bIsMe) {
          return aIsMe ? -1 : 1;
        }
        return (a.name ?? '').localeCompare(b.name ?? '');
      })
      .map((assignee) => {
        const name = assignee.name ?? '';

        return {
          employeeUUID: assignee.employee_id,
          label:
            assignee.employee_id === user.uuid
              ? i18n.t('order_inbound.filter_panel.assignee_me', { name })
              : name,
        };
      });
  }, [data?.getSmartOrderInboundAssignees?.assignees, user.uuid]);

  return { error, loading, options };
};
